import type { Logo, LogoBarProps, LogoBarSize } from "@emkit/builder-widgets";
import { useEditor } from "../../store.js";
import {
  CheckboxField,
  ColorField,
  NumberField,
  SelectField,
  TextField,
} from "./fields.js";
import { MediaField } from "./MediaField.js";
import type { WidgetInspectorProps } from "./registry.js";

const SIZE = ["sm", "md", "lg"] as const;

function newLogoId(): string {
  return `lg-${Math.random().toString(36).slice(2, 8)}`;
}

export function LogoBarInspector({ widget }: WidgetInspectorProps) {
  const update = useEditor((s) => s.updateWidgetProps);
  const p = widget.props as LogoBarProps;
  const logos = p.logos ?? [];

  const setLogos = (next: Logo[]) => update(widget.id, { logos: next });

  const addLogo = () =>
    setLogos([
      ...logos,
      {
        id: newLogoId(),
        src: "https://placehold.co/160x48/e5e7eb/6b7280?text=Logo",
        alt: "Logo",
      },
    ]);

  const updateLogo = (id: string, patch: Partial<Logo>) =>
    setLogos(logos.map((lg) => (lg.id === id ? { ...lg, ...patch } : lg)));

  const removeLogo = (id: string) => setLogos(logos.filter((lg) => lg.id !== id));

  const moveLogo = (id: string, dir: "up" | "down") => {
    const idx = logos.findIndex((lg) => lg.id === id);
    if (idx < 0) return;
    const target = dir === "up" ? idx - 1 : idx + 1;
    if (target < 0 || target >= logos.length) return;
    const next = logos.slice();
    const moved = next.splice(idx, 1)[0];
    if (!moved) return;
    next.splice(target, 0, moved);
    setLogos(next);
  };

  return (
    <>
      <TextField
        label="Title (optional)"
        value={p.title ?? ""}
        onChange={(v) => update(widget.id, { title: v || undefined })}
      />
      <SelectField
        label="Size"
        value={p.size ?? "md"}
        options={SIZE}
        onChange={(v) => update(widget.id, { size: v as LogoBarSize })}
      />
      <NumberField
        label="Gap (px)"
        value={p.gap ?? 40}
        min={0}
        max={200}
        onChange={(v) => update(widget.id, { gap: v })}
      />
      <CheckboxField
        label="Grayscale"
        value={p.grayscale ?? true}
        onChange={(v) => update(widget.id, { grayscale: v })}
      />
      <ColorField
        label="Title colour"
        value={p.textColor ?? "#6b7280"}
        onChange={(v) => update(widget.id, { textColor: v })}
      />

      <div className="emke-section-title" style={{ marginTop: 8 }}>
        Logos ({logos.length})
      </div>

      {logos.map((lg, i) => (
        <div
          key={lg.id}
          style={{
            padding: "8px 16px 10px",
            borderBottom: "1px solid var(--border)",
          }}
        >
          <div
            style={{
              display: "flex",
              gap: 4,
              justifyContent: "flex-end",
              marginBottom: 4,
            }}
          >
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveLogo(lg.id, "up")}
              disabled={i === 0}
              title="Move up"
              style={{ padding: "2px 6px", opacity: i === 0 ? 0.4 : 1 }}
            >
              ↑
            </button>
            <button
              type="button"
              className="emke-btn"
              onClick={() => moveLogo(lg.id, "down")}
              disabled={i === logos.length - 1}
              title="Move down"
              style={{
                padding: "2px 6px",
                opacity: i === logos.length - 1 ? 0.4 : 1,
              }}
            >
              ↓
            </button>
            <button
              type="button"
              className="emke-btn is-danger"
              onClick={() => removeLogo(lg.id)}
              title="Delete"
              style={{ padding: "2px 6px" }}
            >
              ✕
            </button>
          </div>

          <MediaField
            label={`Logo ${i + 1} URL`}
            value={lg.src}
            onChange={(v) => updateLogo(lg.id, { src: v })}
          />
          <TextField
            label="Alt text"
            value={lg.alt}
            onChange={(v) => updateLogo(lg.id, { alt: v })}
          />
          <TextField
            label="Link URL (optional)"
            value={lg.href ?? ""}
            onChange={(v) => updateLogo(lg.id, { href: v || undefined })}
          />
        </div>
      ))}

      <div style={{ padding: "12px 16px 0" }}>
        <button
          type="button"
          className="emke-btn"
          onClick={addLogo}
          style={{ width: "100%" }}
        >
          + Add logo
        </button>
      </div>
    </>
  );
}
